/*
 * PDP media slider
 * Desktop (>=990px): show a single main image at a time and turn the side
 * thumbnail column into a 6-up rail with up/down arrows.
 *
 * The legacy behaviour (stacked gallery, click-thumbnail-to-scroll) lives in
 * chantecaille.min.js as a click listener delegated on `window`. On desktop we
 * stop the click from reaching it (stopPropagation) and drive a single-image
 * view instead. On mobile the thumbnail column is hidden, so the legacy
 * swipe slider keeps working untouched.
 */
(function () {
  var DESKTOP = '(min-width: 990px)';

  function isDesktop() {
    return window.matchMedia(DESKTOP).matches;
  }

  function mainListFor(thumbsEl) {
    var wrapper = thumbsEl.closest('.product__media-wrapper');
    return wrapper ? wrapper.querySelector('.product__media-list') : null;
  }

  function viewport(thumbsEl) {
    return thumbsEl.querySelector('.product__media-gallery-thumbnail-viewport');
  }

  // Show the main media item that matches a thumbnail's media id and mark the
  // matching thumbnail active. `mediaId` is the full "sectionId-mediaId" value
  // shared by both the thumbnail <li> and the main gallery <li>.
  // `direction` (optional): 1 slides the new image in from the right (next),
  // -1 from the left (prev), 0/undefined = no animation (initial / variant load).
  function activate(thumbsEl, mediaId, direction) {
    var mainList = mainListFor(thumbsEl);
    if (!mainList) return;

    var mainItems = mainList.querySelectorAll('.product__media-item');
    var matched = null;
    mainItems.forEach(function (item) {
      var same = item.getAttribute('data-media-id') === mediaId;
      item.classList.toggle('is-active', same);
      item.classList.remove('slide-next', 'slide-prev');
      if (same) matched = item;
    });
    if (!matched && mainItems[0]) { matched = mainItems[0]; matched.classList.add('is-active'); }

    // Items rendered for a variant carry [data-animate-fade-in] (opacity:0) and
    // are only revealed by the theme's scroll/resize handler that adds `.active`.
    // Without this, switching to such an image via a thumbnail leaves it blank
    // until the user scrolls. Reveal the shown image right away.
    if (matched) matched.classList.add('active');

    if (matched && direction) {
      void matched.offsetWidth; // reflow so the animation restarts every change
      matched.classList.add(direction > 0 ? 'slide-next' : 'slide-prev');
    }

    setActiveThumb(thumbsEl, mediaId);

    scrollActiveIntoView(thumbsEl);
  }

  // Mark the thumbnail active; it is also the rail's only tab stop (roving tabindex).
  function setActiveThumb(thumbsEl, mediaId) {
    thumbsEl.querySelectorAll('[data-variant-thumbnail-container]').forEach(function (li) {
      var sel = li.getAttribute('data-media-id') === mediaId;
      li.classList.toggle('active', sel);
      li.setAttribute('aria-selected', sel ? 'true' : 'false');
      li.setAttribute('tabindex', sel ? '0' : '-1');
    });
  }

  // The media id of the main image currently shown.
  function activeMainId(thumbsEl) {
    var list = mainListFor(thumbsEl);
    var active = list && list.querySelector('.product__media-item.is-active');
    return active ? active.getAttribute('data-media-id') : null;
  }

  // The legacy scroll handler (chantecaille.min.js -> updateActiveThumbnail)
  // marks the thumbnail of the first main item "in viewport". On desktop the
  // inactive main items are display:none with a {0,0,0,0} rect, which it counts
  // as in-viewport, so on every scroll it wrongly resets the active thumbnail
  // to the first one. Re-assert the thumbnail that matches the shown image.
  function correctActiveThumb(thumbsEl) {
    if (!isDesktop()) return;
    var mainId = activeMainId(thumbsEl);
    if (!mainId) return;
    var current = thumbsEl.querySelector('[data-variant-thumbnail-container].active');
    if (current && current.getAttribute('data-media-id') === mainId) return; // already correct
    setActiveThumb(thumbsEl, mainId);
  }

  // If the active (black-bordered) thumbnail is partially or fully out of the
  // rail viewport, scroll the rail so the whole thumbnail is visible.
  function scrollActiveIntoView(thumbsEl) {
    var vp = viewport(thumbsEl);
    var li = thumbsEl.querySelector('[data-variant-thumbnail-container].active');
    if (!vp || !li) return;

    var liRect = li.getBoundingClientRect();
    var vpRect = vp.getBoundingClientRect();
    var delta = 0;
    if (liRect.top < vpRect.top) delta = liRect.top - vpRect.top;
    else if (liRect.bottom > vpRect.bottom) delta = liRect.bottom - vpRect.bottom;
    if (delta) vp.scrollBy({ top: delta, behavior: 'smooth' });
  }

  // Make sure exactly one image is shown. Prefer an item already flagged
  // active by the server, otherwise fall back to the first thumbnail.
  function ensureActive(thumbsEl) {
    var mainList = mainListFor(thumbsEl);
    if (!mainList) return;

    var active = mainList.querySelector('.product__media-item.is-active');
    if (active) {
      activate(thumbsEl, active.getAttribute('data-media-id'));
    } else {
      var firstThumb = thumbsEl.querySelector('[data-variant-thumbnail-container]');
      if (firstThumb) activate(thumbsEl, firstThumb.getAttribute('data-media-id'));
    }
    updateArrows(thumbsEl);
  }

  function stepSize(thumbsEl) {
    var li = thumbsEl.querySelector('.product__media-gallery-thumbnail-list .product__media-item');
    if (!li) return 100;
    var styles = window.getComputedStyle(li.parentElement);
    var gap = parseFloat(styles.rowGap || styles.gap || '0') || 0;
    return li.getBoundingClientRect().height + gap;
  }

  function updateArrows(thumbsEl) {
    var vp = viewport(thumbsEl);
    var up = thumbsEl.querySelector('.thumbnail-nav--up');
    var down = thumbsEl.querySelector('.thumbnail-nav--down');
    if (!vp || !up || !down) return;

    var canScroll = vp.scrollHeight > vp.clientHeight + 1;
    thumbsEl.classList.toggle('thumbnail-nav-enabled', canScroll);
    up.disabled = !canScroll || vp.scrollTop <= 0;
    down.disabled = !canScroll || vp.scrollTop + vp.clientHeight >= vp.scrollHeight - 1;

    // Arrow disabled while focused: move focus to the other arrow.
    var focused = document.activeElement;
    if ((focused === up || focused === down) && focused.disabled) {
      var other = focused === up ? down : up;
      if (!other.disabled) other.focus();
    }
  }

  function onClick(e) {
    var thumbsEl = e.currentTarget;

    var nav = e.target.closest('.thumbnail-nav');
    if (nav) {
      var vp = viewport(thumbsEl);
      if (!vp) return;
      var dir = nav.classList.contains('thumbnail-nav--up') ? -1 : 1;
      vp.scrollBy({ top: dir * stepSize(thumbsEl), behavior: 'smooth' });
      return;
    }

    var container = e.target.closest('[data-variant-thumbnail-container]');
    if (!container || !isDesktop()) return; // let the legacy slider handle mobile
    e.stopPropagation(); // prevent legacy window-level scroll handler
    activateThumb(thumbsEl, container);
  }

  // Show the image for a thumbnail, sliding in the matching direction.
  function activateThumb(thumbsEl, container) {
    var targetId = container.getAttribute('data-media-id');
    var items = mainItemsOf(thumbsEl);
    var curIdx = items.findIndex(function (it) { return it.classList.contains('is-active'); });
    var tgtIdx = items.findIndex(function (it) { return it.getAttribute('data-media-id') === targetId; });
    var dir = (curIdx < 0 || tgtIdx < 0 || tgtIdx === curIdx) ? 0 : (tgtIdx > curIdx ? 1 : -1);
    activate(thumbsEl, targetId, dir);
  }

  // Listbox keyboard nav on the thumbnail rail: arrows/Home/End move focus and
  // select (single-select listbox follows focus); Enter/Space also select.
  function onKeydown(e) {
    var thumbsEl = e.currentTarget;
    var li = e.target.closest('[data-variant-thumbnail-container]');
    if (!li || !isDesktop()) return;

    var items = Array.prototype.slice.call(
      thumbsEl.querySelectorAll('[data-variant-thumbnail-container]')
    );
    var idx = items.indexOf(li);
    var last = items.length - 1;
    var key = e.key;
    var next = null;

    if (key === 'ArrowRight' || key === 'ArrowDown') next = items[idx + 1] || items[0];
    else if (key === 'ArrowLeft' || key === 'ArrowUp') next = items[idx - 1] || items[last];
    else if (key === 'Home') next = items[0];
    else if (key === 'End') next = items[last];
    else if (key === 'Enter' || key === ' ' || key === 'Spacebar') next = li;
    else return;

    e.preventDefault();
    activateThumb(thumbsEl, next); // setActiveThumb gives `next` tabindex 0
    next.focus();
  }

  function bindViewportScroll(thumbsEl) {
    var vp = viewport(thumbsEl);
    if (vp && !vp.dataset.scrollBound) {
      vp.dataset.scrollBound = '1';
      vp.addEventListener('scroll', function () { updateArrows(thumbsEl); }, { passive: true });
    }
  }

  // ---- Click + drag the main image left/right to switch (desktop only) ----
  var drag = null;

  function mainItemsOf(thumbsEl) {
    var list = mainListFor(thumbsEl);
    return list ? Array.prototype.slice.call(list.querySelectorAll('.product__media-item')) : [];
  }

  function switchBy(thumbsEl, dir) {
    var items = mainItemsOf(thumbsEl);
    if (items.length < 2) return;
    var idx = items.findIndex(function (it) { return it.classList.contains('is-active'); });
    if (idx < 0) idx = 0;
    var next = idx + dir;
    if (next < 0 || next > items.length - 1) return; // clamp at the ends
    activate(thumbsEl, items[next].getAttribute('data-media-id'), dir);
  }

  function bindDrag(thumbsEl) {
    var list = mainListFor(thumbsEl);
    if (!list || list.dataset.dragBound) return;
    list.dataset.dragBound = '1';

    list.addEventListener('pointerdown', function (e) {
      if (!isDesktop() || e.button !== 0 || mainItemsOf(thumbsEl).length < 2) return;
      e.preventDefault(); // avoid native image-drag / text selection
      drag = { thumbsEl: thumbsEl, startX: e.clientX, startY: e.clientY };
    });
  }

  function onPointerUp(e) {
    if (!drag) return;
    var d = drag;
    drag = null;
    var dx = e.clientX - d.startX;
    var dy = e.clientY - d.startY;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      switchBy(d.thumbsEl, dx < 0 ? 1 : -1); // drag left -> next, right -> prev
    }
  }

  function setup(thumbsEl) {
    if (!thumbsEl.dataset.sliderBound) {
      thumbsEl.dataset.sliderBound = '1';
      thumbsEl.addEventListener('click', onClick);
      thumbsEl.addEventListener('keydown', onKeydown);

      // Variant changes replace the inner HTML (new thumbnails + new viewport),
      // so re-sync the active image and re-bind the fresh viewport.
      var observer = new MutationObserver(function () {
        bindViewportScroll(thumbsEl);
        bindDrag(thumbsEl);
        ensureActive(thumbsEl);
      });
      observer.observe(thumbsEl, { childList: true, subtree: true });

      // Self-heal the active thumbnail when the legacy scroll handler flips it
      // to the first thumbnail (see correctActiveThumb). Runs as a microtask
      // after the class change, so the wrong state never paints.
      var classObserver = new MutationObserver(function () { correctActiveThumb(thumbsEl); });
      classObserver.observe(thumbsEl, { attributes: true, attributeFilter: ['class'], subtree: true });

      window.addEventListener('resize', function () { updateArrows(thumbsEl); }, { passive: true });
    }
    bindViewportScroll(thumbsEl);
    bindDrag(thumbsEl);
    ensureActive(thumbsEl);
  }

  function init() {
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    document.querySelectorAll('media-gallery-thumbnails').forEach(setup);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
